import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  getWeekColumnData,
  getWeeklyStatsAvg,
  getStartingCalendarDayAdjustment,
} from "../../../../../utils/annual-planner-helpers";
import { parseTime } from "../../../calendar/sleep-time/SleepTime";

export const StatsCard = (
  {
    month,
    startingCalendarDay,
    numberOfWeeks,
    athleteStatsState,
    athleteVolumesState,
    isAnnualPlanner = true,
  },
  { locals: activeUser }
) => {
  const { units } = activeUser;

  const { monthFirstDay, monthLastDay } = getMonthRange(
    moment(month),
    startingCalendarDay
  );

  // Getting the athlete volumes corresponding to the current month
  const monthVolumes =
    (athleteVolumesState &&
      athleteVolumesState.filter((volume) =>
        moment(volume.utcDate, "YYYYMMDD").isBetween(
          monthFirstDay,
          monthLastDay,
          undefined,
          []
        )
      )) ||
    [];

  // Getting the athlete stats corresponding to the current month
  const monthStats =
    athleteStatsState &&
    athleteStatsState.filter((stat) =>
      moment(stat.utcDate, "YYYYMMDD").isBetween(
        monthFirstDay,
        monthLastDay,
        undefined,
        []
      )
    );

  const hiddenStats = moment.utc().isBefore(moment(monthFirstDay));

  return (
    <div className={`stats-card`}>
      {/* We map a column for each week, containing the weekly summary for the athlete stats */}
      {Array.from(Array(numberOfWeeks).keys(), (weekNumber, index) => {
        const weekFirstDay = getWeekColumnData(
          month,
          startingCalendarDay,
          weekNumber
        );
        const weekLastDay = moment(weekFirstDay).add(6, "days");

        // Filtering the current week volumnes from the month array
        const currentWeekVolumes = monthVolumes.filter((volume) =>
          moment(volume.utcDate, "YYYYMMDD").isBetween(
            moment(weekFirstDay),
            moment(weekLastDay),
            undefined,
            "[]"
          )
        );

        // Filtering the current week stats from the month array
        const currentWeekStats = monthStats.filter((stat) =>
          moment(stat.utcDate, "YYYYMMDD").isBetween(
            moment(weekFirstDay),
            moment(weekLastDay),
            undefined,
            "[]"
          )
        );

        // Filtering the previous week stats from the month array
        let previousWeekStats = athleteStatsState.filter((stat) =>
          moment(String(stat.utcDate)).isBetween(
            moment(weekFirstDay).subtract(7, "days"),
            moment(weekLastDay).subtract(7, "days"),
            undefined,
            "[]"
          )
        );

        return (
          <StatsColumn
            key={`stats-column-${index}-${Math.random().toString(16).slice(2)}`}
            currentWeekVolumes={currentWeekVolumes}
            currentWeekStats={currentWeekStats}
            hiddenStats={hiddenStats}
            isAnnualPlanner={isAnnualPlanner}
            previousWeekStats={previousWeekStats}
            units={units}
          />
        );
      })}
    </div>
  );
};

StatsCard.contextTypes = { locals: PropTypes.object };

const StatsColumn = ({
  currentWeekStats,
  currentWeekVolumes,
  hiddenStats,
  isAnnualPlanner,
  previousWeekStats,
  units = "metric",
}) => {
  // Getting the weekly averages or sums to each stat
  const {
    volumes,
    readinessAvg,
    sessionRPEAvg,
    bodyWeightAvg,
    kCalAvg,
    stepsAvg,
    sleepAvg,
    bodyWeightDiff,
  } = getWeeklyStatsAvg(
    currentWeekStats,
    currentWeekVolumes,
    previousWeekStats,
    units
  );

  // We render the stat or a "-" if there is no data in the week
  return isAnnualPlanner ? (
    <div className={`stats-column${hiddenStats ? " hidden" : ""}`}>
      <span>{readinessAvg || "-"}</span>
      <span>{sessionRPEAvg || "-"}</span>
      <span>{volumes || "-"}</span>
      <span>{bodyWeightAvg || "-"}</span>
      <span>{kCalAvg || "-"}</span>
      <span>{stepsAvg || "-"}</span>
    </div>
  ) : (
    <div className={`stats-column${hiddenStats ? " hidden" : ""}`}>
      <span>{bodyWeightAvg || "-"}</span>
      <span>{bodyWeightDiff || "-"}</span>
      <span>{stepsAvg || "-"}</span>
      <span>{sleepAvg ? parseTime(sleepAvg, "hours") : "-"}</span>
    </div>
  );
};

export const getMonthRange = (month, startingCalendarDay) => {
  // Getting the first day of the month, and the days amount necessary for the starting calendar day adjustment
  const monthFirstDay = moment(month).startOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const startAmout = getStartingCalendarDayAdjustment(
    monthFirstDay,
    startingCalendarDay
  );

  monthFirstDay.startOf("week").add(startAmout, "days");

  // Getting the last day of the month, and the days amount necessary for the starting calendar day adjustment
  const monthLastDay = moment(month).endOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const endAmount = getStartingCalendarDayAdjustment(
    monthLastDay,
    startingCalendarDay
  );

  monthLastDay.endOf("week").add(endAmount, "days");

  // Ensure that we are not taking into account the first week of the next month if it includes current month days
  monthLastDay.month() !== moment(month).month() &&
    monthLastDay.subtract(1, "week");

  return { monthFirstDay, monthLastDay };
};

//    [    D,    L,    M,    Mie,    J,    V,    S     ]
//
//         |     º
//    [    D,    L,    M,    Mie,    J,    V,    S     ]
//
//
//    [    D,    L,    M,    Mie,    J,    V,    S     ]
//
